import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import '../productPage/producthome.css';
import { Box } from '@mui/material';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { useLocation, useNavigate } from 'react-router-dom';
import BreadCrumbs from '../common/BreadCrumbs';
import IndustrialOverviewPage from './IndustrialOverviewPage';
import ToolPage from '../resourcesPage/simulationPage/ToolPage';
import TechnicalResources from '../automotivePage/TechnicalResources';
import TabsComponent, { GetResponsiveWidth } from '../common/TabsComponent';
import ReactiveBanner from '../common/ReactiveBanner';
import { getTabTypography } from '../investorPage/investorRelationHome';
import { ApplicationsContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';
import { useSalesAndResourcesSimulationToolsContent } from '../../graphql/pimcore/modules/siteContent/hooks/useSalesAndResourcesSimulationToolsContent';
import { InternalLinkSection } from '../common/internalLinkSection';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`product-tabpanel-${index}`}
            aria-labelledby={`product-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

interface IndustrialHomeProps {
    contentTree: ApplicationsContent;
}

const IndustrialHome: FunctionComponent<IndustrialHomeProps> = ({ contentTree }) => {
    const [value, setValue] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    const [openMenu, setOpenMenu] = React.useState(false);
    const { contentTree: toolsContent } = useSalesAndResourcesSimulationToolsContent();
    const { temperatureSensorLinks } = contentTree.Industrial;
    const hasTemperatureSensorLinks = temperatureSensorLinks.links.length > 0;

    const pathMap = useMemo(
        () => [
            '/applications/industrial/',
            '/applications/industrial/technical-resources',
            '/applications/industrial/simulation-tools',
        ],
        []
    );

    const cmsTabsContent = contentTree.Industrial.tabs;

    useEffect(() => {
        const newValue = pathMap.indexOf(location.pathname);
        setValue(newValue >= 0 ? newValue : 0);
    }, [location.pathname, pathMap]);

    const handleChange = (event: React.SyntheticEvent | null, newValue: number) => {
        setValue(newValue);
        const path = pathMap[newValue] ?? pathMap[0];
        if (path) {
            navigate(path);
        }
    };
    return (
        <div key={location.pathname} style={styles.productPageContainer}>
            <div style={styles.breadCrumbs}>
                <Box
                    sx={{
                        width: GetResponsiveWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: 'auto',
                    }}
                >
                    <BreadCrumbs navigationPath="/Home/Applications/Industrial" />
                </Box>
            </div>
            <ReactiveBanner
                title={contentTree.Industrial.pageTitle.content}
                description={contentTree.Industrial.pageSubtitle.content}
                bannerImage={contentTree.Industrial.pageTitleBackgroundImage.imageUrl}
            />

            <Box sx={{ width: '100%', backgroundColor: '#7A7C81' }} data-testid={'outter_box'}>
                <TabsComponent
                    isMenuExpandMoreIcon
                    url={value}
                    setOpenMenu={setOpenMenu}
                    openMenu={openMenu}
                    handleChange={handleChange}
                    menuItems={[
                        { label: cmsTabsContent.Overview.title.content, value: 0 },
                        { label: cmsTabsContent.Resources.title.content, value: 1 },
                        { label: cmsTabsContent.SimulationTools.title.content, value: 2 },
                    ]}
                    tabTypography={{
                        ...getTabTypography,
                    }}
                />
                <CustomTabPanel value={value} index={0}>
                    <IndustrialOverviewPage contentTree={contentTree} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                    <TechnicalResources isAutomotive={false} backgroundColor={'white'} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={2}>
                    <ToolPage cmsContent={toolsContent.Overview} />
                </CustomTabPanel>
            </Box>
            {hasTemperatureSensorLinks ? (
                <InternalLinkSection
                    section={temperatureSensorLinks}
                    backgroundColor="white"
                    contentPadding={{
                        width: GetResponsiveWidth,
                        margin: 'auto',
                        alignItems: 'flex-start',
                    }}
                />
            ) : null}
        </div>
    );
};

export default IndustrialHome;
